/**
 * CORE-86 — the single place that decides whether an AI message may render
 * quick-reply chips.
 *
 * Product rule (Tim, 2026-05-15): one turn offers the user exactly ONE way to
 * pick an option, and that affordance never duplicates the prose.
 *   - inline highlights (`agape_highlights`) and chips (`quick_replies`) are
 *     mutually exclusive — highlights win, they live inside the sentence;
 *   - a message carrying content cards (`agape_cards`) uses highlights only,
 *     never chips — cards already occupy the "pick one" region below the text;
 *   - chips are a *set* of alternatives, so fewer than two is not a choice but
 *     a restatement of the message; the backend only ever emits >= 2;
 *   - a chip must not restate the prose — a label already spelled out in the
 *     body is a duplicate, not an alternative.
 *
 * awesome-chat-api's `_build_outgoing_extra_data` / `_dedup_quick_replies`
 * enforce these server side. This gate is the client safety-net for payloads
 * that slip through — an older message already stored with both fields, a
 * prompt regression, or a backend rollback.
 *
 * On the last rule the client net is deliberately *coarser and narrower* than
 * the server: the backend strips a chip that repeats a bold or highlight span,
 * for which it has the span metadata; the client only has plain `text`, so it
 * can only catch a whole-phrase, multi-word restatement of the body. That is
 * enough for the QA repro (CORE-86, 2026-08-12: prose enumerated the two
 * options with no span, so the server rule never fired) while staying off the
 * false positives that a naive substring match would create — see
 * `chipDuplicatesText` for the guards.
 *
 * Pure and dependency-free so both render paths (web `AgapellaChatThread`,
 * native `MessageBubble`) share one implementation and it stays unit-testable.
 */

/** The subset of `ThreadMessage` the gate reads. */
export interface QuickReplyGateMessage {
  aiGenerated?: boolean;
  quickReplies?: string[] | null;
  highlights?: unknown[] | null;
  cards?: unknown[] | null;
  /** The message body — read only to drop chips that restate the prose. */
  text?: string | null;
}

/** Minimum chips that still read as a choice rather than a restatement. */
export const MIN_QUICK_REPLIES = 2;

/**
 * Least number of words a label must have before we'll dedup it against the
 * body. Single-word labels ("Да", "Йога", "Медитация") occur too readily in
 * ordinary prose for a whole-body match to be safe, so we never dedup them on
 * the client and leave that (span-aware) call to the backend.
 */
export const MIN_DEDUP_LABEL_WORDS = 2;

/**
 * Fold text for a boundary-aware, engine-portable comparison: lowercase, treat
 * every non-alphanumeric run (punctuation, whitespace) as a separator, unify
 * `ё`/`е`, and pad with single spaces so that `includes` only ever matches on
 * whole-word / whole-phrase boundaries. Kept to ASCII+Cyrillic ranges on
 * purpose — no `\p{…}` or look-behind, which older Hermes/JSC builds reject.
 * Accented Latin (Spanish) folds symmetrically on both sides, so equal phrases
 * still match; the worst case is a conservative miss, never an over-match.
 */
function foldForDedup(value: string): string {
  return ` ${value.toLowerCase().replace(/ё/g, 'е').replace(/[^0-9a-zа-я]+/gi, ' ').trim()} `;
}

/**
 * True when `label` restates the prose in `text` verbatim (case/punctuation
 * folded, token-bounded). Only multi-word labels are considered — see
 * `MIN_DEDUP_LABEL_WORDS`.
 */
export function chipDuplicatesText(label: string, text: string): boolean {
  if (typeof label !== 'string' || typeof text !== 'string' || !text) return false;
  const folded = foldForDedup(label);
  const words = folded.trim();
  if (!words || words.split(' ').length < MIN_DEDUP_LABEL_WORDS) return false;
  return foldForDedup(text).includes(folded);
}

/**
 * The quick-reply chips a `message` may actually render, after every exclusion
 * rule — `[]` when it must show none. This is the single source of truth; the
 * boolean gate and the web turn selector both defer to it so the two render
 * paths can't drift.
 *
 * Deliberately defensive about shape: messages reach this from two mappers and
 * an absent field must read as "no affordance", never as a crash.
 */
export function visibleQuickReplies(message?: QuickReplyGateMessage | null): string[] {
  if (!message) return [];

  const options = message.quickReplies;
  if (!Array.isArray(options)) return [];

  // Highlights win — they are rendered inside the body, chips would repeat them.
  if (Array.isArray(message.highlights) && message.highlights.length > 0) return [];

  // Content cards are the choice for this turn; chips below them crowd the UI.
  if (Array.isArray(message.cards) && message.cards.length > 0) return [];

  const text = typeof message.text === 'string' ? message.text : '';
  const kept = text
    ? options.filter((label) => !chipDuplicatesText(label, text))
    : options;

  // A lone survivor is a restatement, not a set of alternatives.
  return kept.length >= MIN_QUICK_REPLIES ? [...kept] : [];
}

/**
 * True when `message` may render its quick-reply chips at all.
 */
export function shouldShowQuickReplies(message?: QuickReplyGateMessage | null): boolean {
  return visibleQuickReplies(message).length > 0;
}

/**
 * The chips a thread-level chip bar should render, or `[]` for none.
 *
 * A bar that hangs above the composer belongs to the *turn*, not to a single
 * message, so it needs two things the per-message gate can't express:
 *
 *  - **The turn ends when the user speaks.** Chips offered before the user
 *    typed their own answer are stale; the Stream-era component hid them and
 *    the ChatApi rewrite lost that. The current turn is therefore the trailing
 *    run of AI messages — everything after the user's last message.
 *  - **Split delivery.** When a turn recommends content the backend splits it
 *    into two messages: cards ride the first, the follow-up prose the second.
 *    The rule is "no chips anywhere in a turn that recommended content", so a
 *    card on *any* message of the turn suppresses the bar, not just the last.
 */
export function selectTurnQuickReplies(
  messages: readonly QuickReplyGateMessage[] | null | undefined,
): string[] {
  if (!Array.isArray(messages) || messages.length === 0) return [];

  const turn: QuickReplyGateMessage[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!messages[i]?.aiGenerated) break;
    turn.unshift(messages[i]);
  }
  if (turn.length === 0) return [];

  if (turn.some((m) => Array.isArray(m.cards) && m.cards.length > 0)) return [];

  const last = turn[turn.length - 1];
  return visibleQuickReplies(last);
}
